import type { ElementType, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../cn.js';

interface SectionRevealProps {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: ElementType;
}

const MAX_DELAY_MS = 600;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function hasIO(): boolean {
  return typeof window !== 'undefined' && typeof window.IntersectionObserver !== 'undefined';
}

/**
 * Scroll-reveal wrapper. Default state: `opacity:0; translateY(8px)`. Once the
 * element crosses 15% into the viewport, transitions to opacity:1 over 520ms
 * with `--ease-resonance`. Fires once.
 *
 * Falls back to immediate visibility when:
 *   - `prefers-reduced-motion: reduce`
 *   - `IntersectionObserver` is unavailable (jsdom, very old browsers)
 *
 * `delay` is clamped to 600ms to keep cascading reveals (e.g. onboarding's
 * 10-goal grid) within a perceptual budget.
 */
export function SectionReveal({ children, delay = 0, className, as }: SectionRevealProps) {
  const Tag = (as ?? 'div') as ElementType;
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState<boolean>(() => prefersReducedMotion() || !hasIO());

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const safeDelay = Math.max(0, Math.min(MAX_DELAY_MS, delay));

  return (
    <Tag
      ref={ref}
      className={cn(className)}
      data-revealed={visible ? 'true' : 'false'}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition:
          'opacity var(--t-reveal) var(--ease-resonance), transform var(--t-reveal) var(--ease-resonance)',
        transitionDelay: visible ? `${safeDelay}ms` : '0ms',
        willChange: visible ? undefined : 'opacity, transform',
      }}
    >
      {children}
    </Tag>
  );
}
